import { z } from 'zod';

const required = (label: string) => z.string().trim().min(1, `${label} is required.`);
const dateTime = (label: string) => required(label).refine((value) => !Number.isNaN(Date.parse(value)), `${label} must be a valid date and time.`);
const optionalText = z.string().trim();

function validSchedule(values: { startsAtUtc: string; endsAtUtc: string }, context: z.RefinementCtx) {
  if (!Number.isNaN(Date.parse(values.startsAtUtc)) && !Number.isNaN(Date.parse(values.endsAtUtc)) && Date.parse(values.startsAtUtc) > Date.parse(values.endsAtUtc)) {
    context.addIssue({ code: 'custom', path: ['endsAtUtc'], message: 'Event end must not be before its start.' });
  }
}

const scheduleFields = {
  name: required('Name').max(200, 'Name must be at most 200 characters.'),
  description: z.string().trim().max(4000, 'Description must be at most 4000 characters.'),
  startsAtUtc: dateTime('Start'),
  endsAtUtc: dateTime('End'),
  timeZoneId: required('Time zone'),
  isAllDay: z.boolean()
};

export const createEventSchema = z.object({
  name: scheduleFields.name,
  startsAtUtc: scheduleFields.startsAtUtc,
  endsAtUtc: scheduleFields.endsAtUtc,
  timeZoneId: scheduleFields.timeZoneId,
  isAllDay: scheduleFields.isAllDay
}).superRefine(validSchedule);
export type CreateEventValues = z.infer<typeof createEventSchema>;

export const eventScheduleSchema = z.object(scheduleFields).superRefine(validSchedule);
export type EventScheduleValues = z.infer<typeof eventScheduleSchema>;

const coordinate = (minimum: number, maximum: number, label: string) => z.string().trim().refine((value) => {
  if (value === '') return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum;
}, `${label} must be between ${minimum} and ${maximum}.`);

export const eventVenueSchema = z.object({
  name: optionalText,
  addressLine1: optionalText,
  addressLine2: optionalText,
  city: optionalText,
  postcode: optionalText,
  country: optionalText,
  notes: optionalText,
  latitude: coordinate(-90, 90, 'Latitude'),
  longitude: coordinate(-180, 180, 'Longitude')
}).superRefine((values, context) => {
  if ((values.latitude === '') !== (values.longitude === '')) {
    context.addIssue({ code: 'custom', path: [values.latitude === '' ? 'latitude' : 'longitude'], message: 'Latitude and longitude must be supplied together.' });
  }
});
export type EventVenueValues = z.infer<typeof eventVenueSchema>;

export const eventClientSchema = z.object({
  contactName: optionalText,
  email: z.union([z.literal(''), z.email('Enter a valid email address.')]),
  phone: optionalText,
  company: optionalText,
  notes: optionalText
});
export type EventClientValues = z.infer<typeof eventClientSchema>;

export const jsonBlobSchema = z.string().refine((value) => {
  try {
    const parsed: unknown = JSON.parse(value);
    return Boolean(parsed) && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
}, 'Enter a valid JSON object.');

const wholeNumber = (minimum: number, maximum: number, label: string, allowBlank: boolean) => z.string().trim().refine((value) => {
  if (allowBlank && value === '') return true;
  return /^\d+$/.test(value) && Number(value) >= minimum && Number(value) <= maximum;
}, allowBlank
  ? `${label} must be a whole number of at least ${minimum}, or left blank for unlimited.`
  : `${label} must be a whole number between ${minimum} and ${maximum}.`);

export const eventPrintSettingsSchema = z.object({
  printCapTotal: wholeNumber(1, Number.MAX_SAFE_INTEGER, 'Print cap', true),
  printCopiesMax: wholeNumber(1, 10, 'Maximum copies', false)
});
export type EventPrintSettingsValues = z.infer<typeof eventPrintSettingsSchema>;

export const eventPrivacySettingsSchema = z.object({
  consentRequired: z.boolean(),
  consentMessage: z.string().max(500, 'Consent message must be at most 500 characters.')
});
export type EventPrivacySettingsValues = z.infer<typeof eventPrivacySettingsSchema>;

export const eventWorkflowSchema = z.object({ workflow: jsonBlobSchema });
export const eventBrandingSchema = z.object({ branding: jsonBlobSchema });
export const eventPrivacySchema = z.object({ privacy: jsonBlobSchema });
export type EventWorkflowValues = z.infer<typeof eventWorkflowSchema>;
export type EventBrandingValues = z.infer<typeof eventBrandingSchema>;
export type EventPrivacyValues = z.infer<typeof eventPrivacySchema>;
